/**
 * InvitePlayersPicker (iter123) — Rich player invite section for
 * /group-bookings/new.
 *
 * Replaces the previous unfiltered checkbox list with:
 *   · Recents chip row  — one-tap re-invite of players you've booked with
 *   · Search input      — filter the invitable directory live
 *   · Avatar rows       — visual scan is faster than a name list
 *   · Email invite input — pull in friends not yet on Rally Lab (backend
 *                          sends them a Join Rally Lab magic link)
 *   · Selected count    — "N selected / max"  so the organiser never
 *                          exceeds max_players − 1
 */
import { useEffect, useMemo, useState } from "react";
import { Search, Mail, X, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { buildAvatarUrl } from "@/components/ProfileImageUpload";

function Avatar({ name, avatar_path, size = 32 }) {
  const url = avatar_path ? buildAvatarUrl(avatar_path) : null;
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();
  return (
    <span
      style={{
        width: size, height: size, borderRadius: 999, overflow: "hidden",
        background: "#141414", border: "1px solid #262626",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#CCFF00", fontSize: size < 30 ? 10 : 11, fontFamily: "monospace",
        fontWeight: 700, flexShrink: 0,
      }}
      aria-hidden
    >
      {url ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials || "?"}
    </span>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InvitePlayersPicker({ selectedIds, onIdsChange, selectedEmails, onEmailsChange, maxPlayers }) {
  const [directory, setDirectory] = useState({ recent: [], invitable: [] });
  const [query, setQuery] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/group-bookings/invitee-directory")
      .then((r) => setDirectory(r.data || { recent: [], invitable: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const idsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const emailsSet = useMemo(() => new Set(selectedEmails.map((e) => e.toLowerCase())), [selectedEmails]);
  const totalInvitedCount = selectedIds.length + selectedEmails.length;
  const remaining = Math.max(0, (maxPlayers - 1) - totalInvitedCount);
  const atCap = remaining === 0;

  const idsByEntry = useMemo(() => {
    const map = new Map();
    [...directory.recent, ...directory.invitable].forEach((p) => map.set(p.id, p));
    return map;
  }, [directory]);

  const filteredInvitable = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return directory.invitable.slice(0, 60);
    return directory.invitable
      .filter((p) => (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q))
      .slice(0, 60);
  }, [directory, query]);

  const togglePlayer = (id) => {
    if (idsSet.has(id)) {
      onIdsChange(selectedIds.filter((x) => x !== id));
    } else {
      if (atCap) {
        toast.error(`Max ${maxPlayers - 1} invitees. Bump "Max players" to add more.`);
        return;
      }
      onIdsChange([...selectedIds, id]);
    }
  };

  const addEmail = (raw) => {
    const em = (raw || "").trim().toLowerCase();
    if (!em) return;
    if (!EMAIL_RE.test(em)) { toast.error("Enter a valid email"); return; }
    if (emailsSet.has(em)) { toast.error("Already added"); return; }
    // If the email happens to match someone in the directory, silently
    // convert to an id-invite so the organiser sees the avatar row.
    const match = [...directory.recent, ...directory.invitable].find(
      (p) => (p.email || "").toLowerCase() === em,
    );
    if (match) {
      togglePlayer(match.id);
      setEmailInput("");
      return;
    }
    if (atCap) {
      toast.error(`Max ${maxPlayers - 1} invitees. Bump "Max players" to add more.`);
      return;
    }
    onEmailsChange([...selectedEmails, em]);
    setEmailInput("");
  };

  const removeEmail = (em) => onEmailsChange(selectedEmails.filter((x) => x !== em));

  const selectedRows = selectedIds.map((id) => idsByEntry.get(id)).filter(Boolean);

  return (
    <div className="card-surface p-4 sm:p-5 space-y-4" data-testid="invite-players-picker">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#CCFF00] flex items-center gap-2">
            <UserPlus className="w-3.5 h-3.5" /> Invite Players
          </div>
          <div className="text-[11px] text-[#A3A3A3] mt-1">
            You count as one — organiser is always included.
          </div>
        </div>
        <div
          data-testid="invite-selected-counter"
          className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border"
          style={atCap
            ? { borderColor: "#FF4500", color: "#FF4500" }
            : { borderColor: "#262626", color: "#A3A3A3" }}
        >
          {totalInvitedCount} / {maxPlayers - 1} selected
        </div>
      </div>

      {/* Selected pills (players + emails) */}
      {(selectedRows.length > 0 || selectedEmails.length > 0) && (
        <div className="flex flex-wrap gap-1.5 pb-2 border-b border-[#262626]" data-testid="invite-selected-pills">
          {selectedRows.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => togglePlayer(p.id)}
              data-testid={`invite-selected-pill-${p.id}`}
              className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 text-xs bg-[#CCFF00] text-black font-semibold hover:opacity-80"
            >
              <Avatar name={p.name} avatar_path={p.avatar_path} size={20} />
              {p.name}
              <X className="w-3 h-3" />
            </button>
          ))}
          {selectedEmails.map((em) => (
            <button
              type="button"
              key={em}
              onClick={() => removeEmail(em)}
              data-testid={`invite-selected-email-${em}`}
              className="inline-flex items-center gap-1.5 pl-2 pr-2 py-1 text-xs border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00]/10"
            >
              <Mail className="w-3 h-3" />
              {em}
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      {/* Recents chip row */}
      {directory.recent.length > 0 && (
        <div data-testid="invite-recents-row">
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2">Recently played with</div>
          <div className="flex flex-wrap gap-2">
            {directory.recent.slice(0, 12).map((p) => {
              const on = idsSet.has(p.id);
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => togglePlayer(p.id)}
                  data-testid={`invite-recent-${p.id}`}
                  className="inline-flex items-center gap-2 pl-1 pr-3 py-1 text-xs border transition-colors"
                  style={on
                    ? { borderColor: "#CCFF00", background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }
                    : { borderColor: "#262626", color: "#E5E5E5" }}
                >
                  <Avatar name={p.name} avatar_path={p.avatar_path} size={22} />
                  <span className="truncate max-w-[120px]">{p.name}</span>
                  {on && <Check className="w-3 h-3" strokeWidth={3} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search + browsable list */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2">All players</div>
        <div className="relative">
          <Search className="w-4 h-4 text-[#525252] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="invite-search-input"
            className="w-full bg-[#141414] border border-[#262626] pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#CCFF00]"
          />
        </div>

        <div
          className="mt-2 max-h-56 overflow-y-auto border border-[#262626] divide-y divide-[#262626]"
          data-testid="invite-directory-list"
        >
          {loading && (
            <div className="p-3 text-xs text-[#525252] italic">Loading directory…</div>
          )}
          {!loading && filteredInvitable.length === 0 && (
            <div className="p-3 text-xs text-[#A3A3A3]" data-testid="invite-directory-empty">
              {query
                ? "No matches — invite by email below."
                : "No other players yet. Invite friends by email below."}
            </div>
          )}
          {filteredInvitable.map((p) => {
            const on = idsSet.has(p.id);
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => togglePlayer(p.id)}
                data-testid={`invite-row-${p.id}`}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#141414] transition-colors"
              >
                <Avatar name={p.name} avatar_path={p.avatar_path} size={32} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-white truncate">{p.name}</span>
                  {p.email && <span className="block text-[10px] text-[#525252] truncate">{p.email}</span>}
                </span>
                <span
                  className="w-5 h-5 border inline-flex items-center justify-center shrink-0"
                  style={on
                    ? { borderColor: "#CCFF00", background: "#CCFF00" }
                    : { borderColor: "#525252" }}
                  aria-hidden
                >
                  {on && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Email invite */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2">
          Invite by email — not on Rally Lab yet? No problem.
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="w-4 h-4 text-[#525252] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addEmail(emailInput);
                }
              }}
              placeholder="friend@email.com"
              data-testid="invite-email-input"
              className="w-full bg-[#141414] border border-[#262626] pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#CCFF00]"
            />
          </div>
          <button
            type="button"
            onClick={() => addEmail(emailInput)}
            disabled={!emailInput.trim()}
            data-testid="invite-email-add"
            className="px-4 py-2 text-xs uppercase tracking-wider bg-[#CCFF00] text-black font-bold disabled:opacity-40"
          >
            Add
          </button>
        </div>
        <div className="text-[10px] text-[#525252] mt-1.5">
          We'll email them a Join Rally Lab link — they'll be auto-added to this session once they register.
        </div>
      </div>
    </div>
  );
}
